import React, { useEffect, useState, useRef, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, MessageCircle, Workflow, Database, Brain, Calendar, Sparkles, Mail, ArrowRight, Play, Pause, RotateCcw, Check } from 'lucide-react';
const STAGE_W = 1280;
const STAGE_H = 720;
// =================== Time markers (seconds) ===================
const T = {
  // Step 1: Fade in (particles assembling)
  PARTICLES_IN: 0.3,
  // Step 2: Platform reassembly
  CORE_IN: 2.0,
  CAP_TOP: 3.0,
  CAP_LEFT_1: 3.4,
  CAP_LEFT_2: 3.8,
  CAP_RIGHT_1: 4.2,
  CAP_RIGHT_2: 4.6,
  CAP_BOTTOM: 5.0,
  LINES_DRAW: 5.6,
  // Step 3: Unification
  UNIFY: 7.6,
  CAPTION_ONE_PLATFORM: 8.4,
  // Step 4: Capability lines
  CAP_DISMISS: 10.0,
  LINE_1: 10.6,
  LINE_2: 11.4,
  LINE_3: 12.2,
  LINE_4: 13.0,
  LINE_5: 13.8,
  // Step 5: Final value
  VALUE_DISMISS: 15.6,
  VAL_1: 16.0,
  VAL_2: 16.9,
  VAL_3: 17.8,
  // Step 6: Hero brand
  HERO: 19.6,
  HERO_LINE: 21.2,
  // Step 7: CTA + contact
  CTA_IN: 23.0,
  CONTACT_IN: 24.0
};
export const SLIDE_8_DURATION = 28;
// =================== Capability nodes (around VEXU core) ===================
interface Cap {
  id: string;
  label: string;
  icon: React.ElementType;
  pos: {
    x: number;
    y: number;
  }; // stage coords (top-left of card)
  appearAt: number;
  // unified position (after collapse) — null = stays
  unified?: {
    x: number;
    y: number;
  };
}
const CORE = {
  x: STAGE_W / 2,
  y: STAGE_H / 2
};
const CAPS: Cap[] = [{
  id: 'ai',
  label: 'AI Intelligence',
  icon: Brain,
  pos: {
    x: STAGE_W / 2 - 90,
    y: 140
  },
  appearAt: T.CAP_TOP
}, {
  id: 'calls',
  label: 'Calls',
  icon: Phone,
  pos: {
    x: 240,
    y: 250
  },
  appearAt: T.CAP_LEFT_1
}, {
  id: 'messages',
  label: 'Messages',
  icon: MessageCircle,
  pos: {
    x: 240,
    y: 380
  },
  appearAt: T.CAP_LEFT_2
}, {
  id: 'workflows',
  label: 'Workflows',
  icon: Workflow,
  pos: {
    x: STAGE_W - 240 - 180,
    y: 250
  },
  appearAt: T.CAP_RIGHT_1
}, {
  id: 'knowledge',
  label: 'Knowledge',
  icon: Database,
  pos: {
    x: STAGE_W - 240 - 180,
    y: 380
  },
  appearAt: T.CAP_RIGHT_2
}, {
  id: 'actions',
  label: 'Actions',
  icon: Calendar,
  pos: {
    x: STAGE_W / 2 - 90,
    y: 510
  },
  appearAt: T.CAP_BOTTOM
}];
// =================== Helpers ===================
function clamp01(v: number) {
  return Math.max(0, Math.min(1, v));
}
function easeOut(t: number) {
  return 1 - Math.pow(1 - t, 3);
}
function easeInOut(t: number) {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
}
// =================== Particle (for entry shimmer) ===================
const PARTICLES = Array.from({
  length: 24
}, (_, i) => {
  const angle = i / 24 * Math.PI * 2;
  const radius = 280 + Math.random() * 100;
  return {
    id: i,
    startX: CORE.x + Math.cos(angle) * radius,
    startY: CORE.y + Math.sin(angle) * radius,
    delay: Math.random() * 0.8
  };
});
// =================== Capability card ===================
const CapCard = memo(({
  cap,
  t,
  unified
}: {
  cap: Cap;
  t: number;
  unified: boolean;
}) => {
  const Icon = cap.icon;
  const visible = t >= cap.appearAt;
  const sinceAppear = clamp01((t - cap.appearAt) / 0.6);
  // When unified, all caps slide toward the core and shrink
  const unifyProgress = unified ? clamp01((t - T.UNIFY) / 1.0) : 0;
  const targetX = CORE.x - 90; // converge near core
  const targetY = CORE.y - 30;
  const x = cap.pos.x + (targetX - cap.pos.x) * easeInOut(unifyProgress);
  const y = cap.pos.y + (targetY - cap.pos.y) * easeInOut(unifyProgress);
  const scale = 1 - unifyProgress * 0.7;
  const opacity = visible ? sinceAppear * (1 - unifyProgress) : 0;
  return <motion.div className="absolute" style={{
    left: x,
    top: y,
    zIndex: 5,
    opacity,
    transform: `scale(${scale})`,
    transformOrigin: 'center'
  }} data-id="element-2435">
        <div className="flex items-center gap-3 px-5 py-3.5 bg-white/[0.04] border border-white/10 backdrop-blur-xl rounded-2xl" data-id="element-2436">
          <div className="w-9 h-9 rounded-xl bg-emerald-500/10 flex items-center justify-center" data-id="element-2437">
            <Icon className="w-4 h-4 text-emerald-400" data-id="element-2438" />
          </div>
          <div className="text-sm font-semibold text-white/90 whitespace-nowrap" data-id="element-2439">
            {cap.label}
          </div>
        </div>
      </motion.div>;
});
CapCard.displayName = 'CapCard';
// =================== Connection line (cap → core) ===================
function lineEndpoints(cap: Cap) {
  // Card center
  const cx = cap.pos.x + 90;
  const cy = cap.pos.y + 26;
  return {
    from: {
      x: cx,
      y: cy
    },
    to: {
      x: CORE.x,
      y: CORE.y
    }
  };
}
const ConnLine = memo(({
  cap,
  t
}: {
  cap: Cap;
  t: number;
}) => {
  const {
    from,
    to
  } = lineEndpoints(cap);
  const len = Math.hypot(to.x - from.x, to.y - from.y);
  const drawProg = clamp01((t - T.LINES_DRAW) / 1.2);
  const offset = len * (1 - drawProg);
  const fadeOut = t >= T.UNIFY ? clamp01((t - T.UNIFY) / 1.0) : 0;
  const opacity = (1 - fadeOut) * 0.6;
  return <g style={{
    opacity
  }} data-id="element-2440">
      <line x1={from.x} y1={from.y} x2={to.x} y2={to.y} stroke="#10D96A" strokeWidth={3} opacity={0.15} data-id="element-2441" />
      <line x1={from.x} y1={from.y} x2={to.x} y2={to.y} stroke="#10D96A" strokeWidth={1.5} strokeDasharray={len} strokeDashoffset={offset} opacity={0.85} data-id="element-2442" />
    </g>;
});
ConnLine.displayName = 'ConnLine';
// =================== VEXU Core ===================
const VexuCore = memo(({
  t,
  hero
}: {
  t: number;
  hero: boolean;
}) => {
  const visible = t >= T.CORE_IN;
  const sinceAppear = clamp01((t - T.CORE_IN) / 0.8);
  const pulseIntensity = hero ? 1 : 0.5;
  return <motion.div className="absolute" style={{
    left: CORE.x,
    top: CORE.y,
    transform: 'translate(-50%, -50%)',
    zIndex: 10,
    opacity: visible ? sinceAppear : 0
  }} data-id="element-2443">
      <div className="relative" data-id="element-2444">
        {/* Outer glow rings */}
        <motion.div className="absolute inset-0 rounded-full" style={{
        background: 'radial-gradient(circle, rgba(16,217,106,0.4) 0%, transparent 70%)',
        transform: `scale(${1 + pulseIntensity * 0.5})`
      }} animate={{
        opacity: [0.4, 0.7, 0.4],
        scale: [1.5, 1.8, 1.5]
      }} transition={{
        duration: 3,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-2445" />

        {/* Core circle */}
        <div className={`relative rounded-full bg-gradient-to-br from-emerald-400/30 to-emerald-600/20 border-2 border-emerald-400/40 backdrop-blur-xl flex flex-col items-center justify-center ${hero ? 'w-44 h-44' : 'w-28 h-28'} transition-all duration-1000`} style={{
        boxShadow: '0 0 40px rgba(16,217,106,0.4), inset 0 0 20px rgba(16,217,106,0.15)'
      }} data-id="element-2446">
          <Sparkles className={`text-emerald-300 ${hero ? 'w-10 h-10' : 'w-7 h-7'} transition-all duration-1000`} data-id="element-2447" />
          <div className={`font-bold tracking-[0.3em] text-white mt-2 ${hero ? 'text-base' : 'text-[10px]'} transition-all duration-1000`} data-id="element-2448">
            VEXU
          </div>
        </div>
      </div>
    </motion.div>;
});
VexuCore.displayName = 'VexuCore';
// =================== Capability summary line ===================
const SummaryLine = memo(({
  text,
  start,
  t,
  end
}: {
  text: string;
  start: number;
  t: number;
  end: number;
}) => {
  const fadeIn = clamp01((t - start) / 0.4);
  const fadeOut = clamp01((t - end) / 0.4);
  const opacity = fadeIn * (1 - fadeOut);
  const yOffset = (1 - fadeIn) * 8;
  if (opacity <= 0.01) return null;
  return <motion.div className="flex items-center gap-3" style={{
    opacity,
    transform: `translateY(${yOffset}px)`
  }} data-id="element-2449">
        <div className="w-5 h-5 rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center shrink-0" data-id="element-2450">
          <Check className="w-3 h-3 text-emerald-400" data-id="element-2451" />
        </div>
        <div className="text-2xl font-light tracking-tight text-white/85" data-id="element-2452">
          {text}
        </div>
      </motion.div>;
});
SummaryLine.displayName = 'SummaryLine';
// =================== Word reveal ===================
const WordReveal = memo(({
  text,
  start,
  t,
  size = 'normal',
  color = 'text-white'
}: {
  text: string;
  start: number;
  t: number;
  size?: 'normal' | 'large' | 'huge';
  color?: string;
}) => {
  const words = text.split(' ');
  const sizeClass = size === 'huge' ? 'text-7xl' : size === 'large' ? 'text-5xl' : 'text-4xl';
  return <div className={`flex items-center gap-3 ${sizeClass} font-light tracking-tight ${color}`} data-id="element-2453">
        {words.map((w, i) => {
      const ws = start + i * 0.1;
      const p = clamp01((t - ws) / 0.4);
      return <motion.span key={i} style={{
        filter: `blur(${(1 - p) * 14}px)`,
        opacity: p
      }} data-id="element-2454">
              {w}
            </motion.span>;
    })}
      </div>;
});
WordReveal.displayName = 'WordReveal';
// =================== Main ===================
interface PresentationSlide8Props {
  externalElapsed?: number;
  hideChrome?: boolean;
}
export function PresentationSlide8({
  externalElapsed,
  hideChrome = false
}: PresentationSlide8Props) {
  const [elapsed, setElapsed] = useState(0);
  const [playing, setPlaying] = useState(true);
  const startedAtRef = useRef<number>(Date.now());
  const pausedAtRef = useRef<number>(0);
  const t = externalElapsed !== undefined ? externalElapsed : elapsed;
  useEffect(() => {
    if (externalElapsed !== undefined) return;
    if (!playing) {
      pausedAtRef.current = elapsed;
      return;
    }
    startedAtRef.current = Date.now() - pausedAtRef.current * 1000;
    let raf: number;
    const tick = () => {
      const newT = (Date.now() - startedAtRef.current) / 1000;
      if (newT >= SLIDE_8_DURATION) {
        setElapsed(SLIDE_8_DURATION);
        setPlaying(false);
        return;
      }
      setElapsed(newT);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, externalElapsed]);
  const replay = () => {
    pausedAtRef.current = 0;
    setElapsed(0);
    setPlaying(true);
  };
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const compute = () => {
      const s = Math.min(window.innerWidth / STAGE_W, window.innerHeight / STAGE_H);
      setScale(s);
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);
  // Phase flags
  const showCaps = t >= T.CORE_IN && t < T.CAP_DISMISS;
  const showLines = t >= T.LINES_DRAW && t < T.UNIFY + 1.2;
  const showOnePlatform = t >= T.CAPTION_ONE_PLATFORM && t < T.CAP_DISMISS;
  const showSummary = t >= T.LINE_1 && t < T.VALUE_DISMISS;
  const showValue = t >= T.VAL_1 && t < T.HERO;
  const showHero = t >= T.HERO;
  const showHeroLine = t >= T.HERO_LINE;
  const showCta = t >= T.CTA_IN;
  const showContact = t >= T.CONTACT_IN;
  // Core stays visible most of the slide; in summary/value phases hide it
  const coreVisible = t >= T.CORE_IN && t < T.CAP_DISMISS ||
  // assembly + unification
  showHero; // re-emerges hero
  return <div className="relative w-screen h-screen overflow-hidden bg-[#06080F]" data-id="element-2455">
      {/* Ambient blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-2456">
        <motion.div className="absolute w-[900px] h-[900px] rounded-full opacity-[0.04]" style={{
        background: 'radial-gradient(circle, #10D96A 0%, transparent 70%)',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%,-50%)'
      }} animate={{
        scale: [1, 1.1, 1]
      }} transition={{
        duration: 8,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-2457" />
      </div>

      {/* Scaled stage */}
      <div className="absolute inset-0 flex items-center justify-center" data-id="element-2458">
        <div className="relative origin-center" style={{
        width: STAGE_W,
        height: STAGE_H,
        transform: `scale(${scale})`
      }} data-id="element-2459">
          {/* Entry particles */}
          {t < T.CORE_IN + 0.5 && PARTICLES.map(p => {
          const localT = clamp01((t - T.PARTICLES_IN - p.delay) / 1.5);
          const x = p.startX + (CORE.x - p.startX) * easeOut(localT);
          const y = p.startY + (CORE.y - p.startY) * easeOut(localT);
          const opacity = localT < 0.9 ? localT : (1 - localT) * 10;
          return <div key={p.id} className="absolute w-1 h-1 rounded-full bg-emerald-300" style={{
            left: x,
            top: y,
            opacity: clamp01(opacity),
            boxShadow: '0 0 8px rgba(16,217,106,0.9)'
          }} data-id="element-2460" />;
        })}

          {/* Connection lines layer (z:2) */}
          {showLines && <svg className="absolute inset-0 pointer-events-none" style={{
          zIndex: 2
        }} viewBox={`0 0 ${STAGE_W} ${STAGE_H}`} data-id="element-2461">
              {CAPS.map(cap => <ConnLine key={cap.id} cap={cap} t={t} data-id="element-2462" />)}
            </svg>}

          {/* Capability cards */}
          {showCaps && CAPS.map(cap => <CapCard key={cap.id} cap={cap} t={t} unified={t >= T.UNIFY} data-id="element-2463" />)}

          {/* VEXU Core */}
          {coreVisible && <VexuCore t={t} hero={showHero} data-id="element-2464" />}

          {/* "One platform." caption */}
          <AnimatePresence data-id="element-2465">
            {showOnePlatform && <motion.div key="one-platform" initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -8
          }} transition={{
            duration: 0.6,
            ease: [0.16, 1, 0.3, 1]
          }} className="absolute left-1/2 -translate-x-1/2 text-center" style={{
            top: 80,
            zIndex: 30
          }} data-id="element-2466">
                <div className="text-3xl font-light tracking-tight text-white/85" data-id="element-2467">
                  One platform.
                </div>
              </motion.div>}
          </AnimatePresence>

          {/* Capability summary lines */}
          {showSummary && <div className="absolute left-1/2 -translate-x-1/2 flex flex-col gap-5" style={{
          top: 180,
          zIndex: 30
        }} data-id="element-2468">
              <SummaryLine text="Handle every conversation" start={T.LINE_1} end={T.VALUE_DISMISS} t={t} data-id="element-2469" />
              <SummaryLine text="Across every channel" start={T.LINE_2} end={T.VALUE_DISMISS} t={t} data-id="element-2470" />
              <SummaryLine text="With full context" start={T.LINE_3} end={T.VALUE_DISMISS} t={t} data-id="element-2471" />
              <SummaryLine text="In real time" start={T.LINE_4} end={T.VALUE_DISMISS} t={t} data-id="element-2472" />
              <SummaryLine text="With intelligent actions" start={T.LINE_5} end={T.VALUE_DISMISS} t={t} data-id="element-2473" />
            </div>}

          {/* Final value statement */}
          {showValue && <div className="absolute inset-0 flex flex-col items-center justify-center gap-5" style={{
          zIndex: 30
        }} data-id="element-2474">
              <WordReveal text="Simplify AI." start={T.VAL_1} t={t} size="large" color="text-white/55" data-id="element-2475" />
              <WordReveal text="Unify communication." start={T.VAL_2} t={t} size="large" color="text-white/75" data-id="element-2476" />
              <WordReveal text="Activate your business." start={T.VAL_3} t={t} size="huge" color="text-emerald-400" data-id="element-2477" />
            </div>}

          {/* Hero brand line — perfectly centered below VEXU core */}
          {showHeroLine && <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: [0.16, 1, 0.3, 1]
        }} className="absolute inset-x-0 flex justify-center" style={{
          top: STAGE_H / 2 + 140,
          zIndex: 30
        }} data-id="element-2478">
              <div className="text-2xl font-light tracking-tight text-white/70 text-center" data-id="element-2479">
                The future of customer interaction.
              </div>
            </motion.div>}

          {/* Contact details — perfectly centered below hero line */}
          {showContact && <motion.div initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          ease: [0.16, 1, 0.3, 1],
          delay: 0.1
        }} className="absolute inset-x-0 flex items-center justify-center gap-8" style={{
          top: STAGE_H / 2 + 210,
          zIndex: 35
        }} data-id="element-2480">
              <div className="flex items-center gap-2.5" data-id="element-2481">
                <div className="w-8 h-8 rounded-full bg-white/[0.04] border border-white/10 flex items-center justify-center" data-id="element-2482">
                  <Mail className="w-3.5 h-3.5 text-emerald-400" data-id="element-2483" />
                </div>
                <div className="text-sm font-medium text-white/80" data-id="element-2484">
                  fadi@vexu.ai
                </div>
              </div>
              <div className="w-px h-6 bg-white/10" data-id="element-2485" />
              <div className="flex items-center gap-2.5" data-id="element-2486">
                <div className="w-8 h-8 rounded-full bg-white/[0.04] border border-white/10 flex items-center justify-center" data-id="element-2487">
                  <Phone className="w-3.5 h-3.5 text-emerald-400" data-id="element-2488" />
                </div>
                <div className="text-sm font-medium text-white/80" data-id="element-2489">
                  +971 58 537 7090
                </div>
              </div>
            </motion.div>}
        </div>
      </div>

      {/* Chrome */}
      {!hideChrome && <>
          <div className="absolute top-6 right-6 z-[80] flex items-center gap-2" data-id="element-2490">
            <button onClick={() => setPlaying(p => !p)} className="flex items-center gap-2 px-4 py-2.5 bg-white/[0.06] hover:bg-white/[0.12] backdrop-blur-xl border border-white/10 rounded-full text-xs font-semibold text-white/85 hover:text-white transition-all" data-id="element-2491">
              {playing ? <>
                  <Pause className="w-3.5 h-3.5" data-id="element-2492" /> Pause
                </> : <>
                  <Play className="w-3.5 h-3.5" data-id="element-2493" /> Play
                </>}
            </button>
            <button onClick={replay} className="flex items-center gap-2 px-4 py-2.5 bg-white/[0.06] hover:bg-white/[0.12] backdrop-blur-xl border border-white/10 rounded-full text-xs font-semibold text-white/85 hover:text-white transition-all" data-id="element-2494">
              <RotateCcw className="w-3.5 h-3.5" data-id="element-2495" /> Replay
            </button>
          </div>

          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-[80] w-full max-w-2xl px-6" data-id="element-2496">
            <div className="relative h-1.5 rounded-full bg-white/[0.07] overflow-hidden" data-id="element-2497">
              <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-emerald-400 via-emerald-300 to-amber-300 rounded-full" style={{
            width: `${t / SLIDE_8_DURATION * 100}%`
          }} data-id="element-2498" />
            </div>
            <div className="flex items-center justify-between mt-2 text-[9px] font-bold tracking-[0.25em] text-white/45 uppercase" data-id="element-2499">
              <span className="font-mono" data-id="element-2500">
                {Math.floor(t / 60)}:
                {String(Math.floor(t % 60)).padStart(2, '0')}
              </span>
              <span data-id="element-2501">The Future</span>
              <span className="font-mono" data-id="element-2502">
                {Math.floor(SLIDE_8_DURATION / 60)}:
                {String(Math.floor(SLIDE_8_DURATION % 60)).padStart(2, '0')}
              </span>
            </div>
          </div>
        </>}
    </div>;
}